import { parseAccessDenialReason } from '@workspace/shared';
import { ShieldOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { useSignOut } from '@/features/auth/hooks/useSignOut';

/**
 * Access-denied screen, routed to on 403s with a machine-readable `reason`
 * (access_expired, missing_word_access, deployment_mismatch). The denial can
 * be lifted server-side, so the session is kept — sign-out is offered but not
 * forced.
 */
export const Forbidden = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const signOutMutation = useSignOut();

  const reason = parseAccessDenialReason(searchParams.get('reason'));

  return (
    <div className="h-viewport flex flex-col items-center justify-center bg-white px-4">
      <img alt="Lucio AI Logo" className="mb-6 h-16 w-auto" src="/assets/lucioSymbol.svg" />

      <div className="flex flex-col items-center text-center">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
          <ShieldOff className="h-6 w-6 text-gray-400" />
        </div>

        <h1 className="text-primary mb-2 text-lg font-semibold">{t('forbidden.title')}</h1>
        <p className="mb-6 max-w-[260px] text-xs text-gray-500">
          {t(reason ? `forbidden.reason.${reason}` : 'forbidden.reason.default')}
        </p>

        <Button
          className="w-full max-w-[260px] border-gray-300 bg-white font-medium text-gray-700 hover:bg-gray-50"
          loading={signOutMutation.isPending}
          onClick={() => signOutMutation.mutate()}
          variant="outline"
        >
          {t('forbidden.signOut')}
        </Button>
      </div>
    </div>
  );
};
